<template>
  <CustomTimeGrid
    :events="legacyEvents"
    :view-type="viewType"
    :current-date="currentDate"
    :accessible-users="accessibleUsers"
    @event-click="onEventClick"
    @event-create="$emit('create', $event)"
    @event-update="$emit('update', $event)"
  />
</template>

<script>
import { computed } from 'vue'
import CustomTimeGrid from '@/components/CustomTimeGrid.vue'

/**
 * 주/일 타임그리드 어댑터.
 *
 * CustomTimeGrid 는 2,000줄이 넘고 sticky 헤더 스크롤 동기화, 자체 행 높이 계산이
 * 있는 종일 밴드, 드래그 생성/이동, 컬럼 겹침 레이아웃, 현재시각 인디케이터를
 * 담고 있다. 재작성은 가용한 작업 중 리스크가 가장 크고 모바일 이득은 가장 작다 —
 * 애초에 390px 에서 주 뷰는 성립하지 않으므로 모바일 문제가 아니다.
 *
 * 그래서 재작성하지 않고 감싼다. 정규화된 AgendaItem 을 이 파일 안에서만
 * 레거시 형태로 되돌려 넘긴다. **이 파일 밖의 누구도 레거시 형태를 모른다.**
 * 그 큰 파일을 건드리지 않고 삼중 형태를 걷어내는 방법이다.
 *
 * 업무는 종일 밴드로 들어간다. 다만 그 밴드의 행 높이 계산이 일정만 가정하고
 * 쓰여 있어서, 문제가 생기면 includeTasks=false 로 잠시 끌 수 있게 열어 둔다.
 *
 * ── 넘기는 형태에 대해 알아 둘 것 ──
 *
 * 정규화된 항목은 **extendedProps.agendaItem 한 곳에만** 담는다.
 * 예전 FullCalendar 규약대로 extendedProps.eventType / .location / .user 를
 * 기대하는 코드가 그리드 쪽에 남아 있었는데(그 자리는 늘 비어 있다), 그래서
 * 시간 블록의 유형색 클래스가 event-undefined 로 붙어 색이 아예 없었고
 * 장소·담당자도 뜨지 않았다. 지금은 그리드가 agendaItem 에서 직접 꺼내 쓴다
 * (CustomTimeGrid 의 itemOf/userOf/locationOf). 여기서 필드를 평평하게 펴는
 * 방식으로 되돌리지 말 것 — 그 삼중 형태를 없애려고 이 어댑터를 만들었다.
 *
 * accessibleUsers 는 **Calendar.vue 가 넘기지 않는다**(기본 []).
 * 이 prop 은 종일 밴드에서 사람별 줄 순서를 정하는 데만 쓰이는데, 예전에는
 * 이 목록에 없는 사람의 항목이 줄을 못 받아 통째로 버려졌다 — 그래서 주/일 뷰의
 * 종일 밴드가 늘 비어 있었다. 지금은 그리드가 목록이 비면 이벤트에서 직접
 * 사람을 모아 줄을 세우므로 prop 없이도 동작한다. 이 prop 은 "이 순서로 세워라"
 * 는 선택적 힌트로만 남아 있다.
 */
export default {
  name: 'CalendarTimeGrid',
  components: { CustomTimeGrid },
  props: {
    items: { type: Array, default: () => [] },
    viewType: { type: String, default: 'week' },
    currentDate: { type: Date, required: true },
    accessibleUsers: { type: Array, default: () => [] },
    includeTasks: { type: Boolean, default: true }
  },
  emits: ['item-click', 'create', 'update'],
  setup(props, { emit }) {
    const legacyEvents = computed(() =>
      props.items
        .filter((it) => (props.includeTasks ? true : it.kind === 'schedule'))
        .map((it) => ({
          id: it.uid, // 두 종류가 id 공간을 공유하므로 uid 를 쓴다
          title: it.title,
          start: it.start,
          end: it.end,
          startDate: it.startAt,
          endDate: it.endAt,
          allDay: it.allDay,
          isAllDay: it.allDay,
          // 업무는 시각이 없으면 종일 밴드로 간다
          eventType: it.kind === 'schedule' ? it.eventType : 'work',
          backgroundColor: it.color,
          color: it.color,
          user: it.owner,
          userId: it.owner?.id,
          extendedProps: { agendaItem: it }
        }))
    )

    function onEventClick(legacy) {
      const item = legacy?.extendedProps?.agendaItem
      if (item) emit('item-click', item)
    }

    return { legacyEvents, onEventClick }
  }
}
</script>
